/* eslint-disable max-len */
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { setActiveModalAction } from '../../../actions';
import { PizzaSizer } from '../../../components';
import { useOnClickOutside } from '../../../hooks';
import { fromStore } from '../../../selectors';
import { AddCartButton, FavotiteButton, GoodPrice } from '../../card';
import {
  getIdModGood, getImageUrl, getModifiedGood, getWeightAndMeasure,
} from '../../../utils';

export function DetailModal() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const ref = useRef();
  const refInfo = useRef();

  const goodId = useSelector(fromStore.detailGoodModal);
  const good = useSelector(fromStore.goodsByIdSelector(goodId));
  const categoryIdPizza = useSelector(fromStore.getCategorySettingsId('pizza'));
  const categories = useSelector(fromStore.denormalizedCategories);

  const isPizza = good.category.id === categoryIdPizza && (!good.isGroup || isEmpty(good.items));
  const isGroup = good.isGroup && !isEmpty(good.items);

  const { modifications = [], items = [] } = good;
  const defaultModification = !isEmpty(modifications)
    && (modifications.find((mod) => mod.default) || modifications[0]);

  const [activeMod, setActiveMod] = useState(defaultModification);
  const [openInfo, setOpenInfo] = useState(false);

  const defaultOption = !isEmpty(items) && (items.find((option) => option.isDefault) || items[0]);
  const [activeOption, setActiveOption] = useState(defaultOption.isStop ? items.find((option) => !option.isStop) : defaultOption);

  let cartId = String(goodId);

  if (isPizza && activeMod) {
    cartId = getIdModGood(activeMod, goodId);
  } else if (isGroup && activeOption) {
    cartId = String(activeOption.id);
  }

  const onClose = () => {
    dispatch(setActiveModalAction({ field: 'detail', value: null }));

    const newPath = categories.find((item) => item.id === good.category.id).key;
    navigate(`/menu/${newPath || 'rolls'}`);
  };

  let item = good;

  if (isPizza && activeMod) {
    item = getModifiedGood(good, activeMod.id);
  } else if (isGroup && activeOption) {
    item = activeOption;
  }

  const weightData = getWeightAndMeasure((item.weight || good.weight), item.measure);

  useOnClickOutside(ref, onClose);

  const handleClickInfoButton = () => {
    setOpenInfo(true);
  };

  const onCloseInfo = () => {
    setOpenInfo(false);
  };

  useOnClickOutside(refInfo, onCloseInfo);

  const hasWeightAndInfo = () => {
    const {
      proteins,
      carbs,
      fats,
      kcal,
      allergens,
    } = (isGroup ? item : good);

    if (!proteins && !carbs && !fats && !kcal && !weightData.weight && (!allergens || allergens === '')) return false;
    return true;
  };

  const renderInfoButton = () => {
    const {
      proteins,
      carbs,
      fats,
      kcal,
      allergens,
    } = (isGroup ? item : good);

    if (proteins || carbs || fats || kcal || (allergens != null && allergens !== '')) {
      return (
        <button type="button" onClick={handleClickInfoButton} className="btn btn-icon btn-bg-light btn-hover-scale w-40px h-40px me-3">
          <span className="svg-icon svg-icon-1">
            <svg fill="#A1A5B7" width="800px" height="800px" viewBox="-160 0 512 512" xmlns="http://www.w3.org/2000/svg"><path d="M20 424.229h20V279.771H20c-11.046 0-20-8.954-20-20V212c0-11.046 8.954-20 20-20h112c11.046 0 20 8.954 20 20v212.229h20c11.046 0 20 8.954 20 20V492c0 11.046-8.954 20-20 20H20c-11.046 0-20-8.954-20-20v-47.771c0-11.046 8.954-20 20-20zM96 0C56.235 0 24 32.235 24 72s32.235 72 72 72 72-32.235 72-72S135.764 0 96 0z" /></svg>
          </span>
        </button>
      );
    }
    return null;
  };

  const renderInfo = () => {
    const {
      proteins,
      carbs,
      fats,
      kcal,
      allergens,
    } = (isGroup ? item : good);

    if (!openInfo) return null;

    return (
      <div>
        <div style={{ width: '250px', position: 'absolute', transform: 'translate(0px, -60px)' }}>
          <div ref={refInfo} className="glass" style={{ flexDirection: 'column', overflowY: 'scroll', height: '135px' }}>
            {allergens != null && allergens !== '' && (
              <div className="glass_text" style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Аллергены</span>
                <span>{allergens}</span>
              </div>
            )}
            <div style={{ color: 'white', opacity: '0.7', marginBottom: '5px' }}>Пищевая ценность на 100 гр.</div>
            {kcal != null && (
              <div className="glass_text" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Энерг. ценность</span>
                <span>{kcal} ккал.</span>
              </div>
            )}
            {proteins != null && (
              <div className="glass_text" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Белки</span>
                <span>{proteins} гр.</span>
              </div>
            )}
            {fats != null && (
              <div className="glass_text" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Жиры</span>
                <span>{fats} гр.</span>
              </div>
            )}
            {carbs != null && (
              <div className="glass_text" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span>Углеводы</span>
                <span>{carbs} гр.</span>
              </div>
            )}
            {weightData.weight && (
              <div className="glass_text" style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{weightData.measure.title}{good.measure === 'pc' ? '' : ' блюда'}</span>
                <span>{`${weightData.weight} ${weightData.measure.value}`}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderPizzaSizer = isPizza && activeMod && (
    <div className="d-flex">
      <div className="border border-gray-300 border-dashed rounded min-w-100px w-100 py-2 px-4 mb-3">
        <div className="d-flex flex-stack">
          <span className="fs-4 text-gray-600 me-5">Размер пиццы</span>
          <PizzaSizer
            items={good.modifications}
            selected={activeMod}
            onChange={(value) => setActiveMod(value)}
          />
        </div>
      </div>
    </div>
  );

  const renderOptionsChanger = isGroup && (
    <div className="d-flex">
      <div className="border border-gray-300 border-dashed rounded min-w-100px w-100 py-2 px-4 mb-3">
        <div className="d-flex flex-column justify-content-center" style={{ gap: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className="fs-4 text-gray-600 me-5">{good.titleChanger || 'Размер пиццы'}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PizzaSizer
              items={items.filter((option) => !option.isStop).sort((a, b) => (b.order || 0) - (a.order || 0)).map((option) => ({ ...option, title: option.groupTitle }))}
              selected={activeOption}
              onChange={(value) => setActiveOption(value)}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderDescription = () => {
    if (item.description) return item.description;
    if (good.description) return good.description;
    return '';
  };

  return (
    <div className="modal fade d-block show">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div ref={ref} className="modal-content">
          <div className="modal-body scroll-y p-0 m-0">
            <div className="card card-flush border-0 h-100">

              <div className="card-body py-9">
                <div className="row gx-9 h-100">

                  <div className="col-lg-6 mb-10 mb-lg-0">
                    <div className="rounded min-w-300px position-relative">
                      <img src={getImageUrl(good.image)} alt={good.name} className="rounded w-100" />
                    </div>
                  </div>

                  <div className="col-lg-6">

                    <div className="d-flex flex-column h-100">

                      <div>
                        <div className="d-flex flex-stack mb-5">
                          <div className="me-5">
                            <div className="d-flex align-items-center mb-2">
                              <span className="badge badge-secondary">{good.category.title}</span>
                              {(isGroup ? item.hot : good.hot) && <img className="ms-3 w-20px" src="/assets/media/pepper.png" alt="острое" />}
                              {(isGroup ? item.vegan : good.vegan) && <img className="ms-3 w-20px" src="/assets/media/vegan.png" alt="веган" />}
                            </div>
                            <span className="text-gray-800 fs-1 lh-sm fw-bolder">{isGroup ? item.name : good.name}</span>
                          </div>

                          <button type="button" onClick={onClose} className="btn btn-light flex-shrink-0 align-self-start fs-7 px-2 py-2">
                            <span className="svg-icon svg-icon-muted svg-icon-1 me-0">
                              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path opacity="0.3" d="M6 19.7C5.7 19.7 5.5 19.6 5.3 19.4C4.9 19 4.9 18.4 5.3 18L18 5.3C18.4 4.9 19 4.9 19.4 5.3C19.8 5.7 19.8 6.29999 19.4 6.69999L6.7 19.4C6.5 19.6 6.3 19.7 6 19.7Z" fill="currentColor" />
                                <path d="M18.8 19.7C18.5 19.7 18.3 19.6 18.1 19.4L5.40001 6.69999C5.00001 6.29999 5.00001 5.7 5.40001 5.3C5.80001 4.9 6.40001 4.9 6.80001 5.3L19.5 18C19.9 18.4 19.9 19 19.5 19.4C19.3 19.6 19 19.7 18.8 19.7Z" fill="currentColor" />
                              </svg>
                            </span>
                          </button>
                        </div>
                        <div className="d-flex align-items-center flex-wrap d-grid gap-2 mb-5">
                          {hasWeightAndInfo() && (
                            <div className="d-flex align-items-center me-5">
                              {renderInfoButton()}
                              {weightData.weight && (
                              <div className="m-0">
                                <span className="fw-bold text-gray-400 d-block fs-8">{weightData.measure.title}</span>
                                <span className="fw-bolder text-gray-800 fs-7">{`${weightData.weight} ${weightData.measure.value}`}</span>
                              </div>
                              )}
                            </div>
                          )}
                          <FavotiteButton id={isGroup ? item.id : goodId} />
                        </div>
                      </div>
                      {renderInfo()}

                      <div className="mb-6">
                        <span className="fw-bold text-gray-600 fs-6 mb-10 d-block">{renderDescription()}</span>
                        {renderPizzaSizer}
                        {renderOptionsChanger}
                      </div>

                      <div className="d-flex flex-stack mt-auto bd-highlight">
                        <GoodPrice price={item.price} oldPrice={item.oldPrice} id={String(item.id)} />
                        <AddCartButton isStop={item.isStop} id={cartId} item={item} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
